import React, { useState } from 'react';
// @ts-ignore
import styles from './PatientsGroupsListItem.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import Button from '../../../../Components/Button/Button';
import { getMomentDate } from '../../../../Utils/dates.util';
import { useAppContext } from '../../../../Services/AppContext';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useTranslation from '../../../../Hooks/useTranslation';
import PatientsGroupsHistoryModal from '../PatientsGroupsHistory/PatientsGroupsHistoryModal';
import ActionCenterNewMessageModal from '../../../ActionCenter/Components/ActionCenterNewMessage/ActionCenterNewMessageModal';
import { MultiSearchWithCheckListItemType } from '../../../Base/Enums/MultiSearchWithCheckListItemType.enum';
import PatientsGroupsRenameModal from '../PatientsGroupsRename/PatientsGroupsRenameModal';
import PatientsGroupsMembersModal from '../PatientsGroupsMembers/PatientsGroupsMembersModal';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';

interface Props {
	item: {
		id: string;
		name: string;
		createdAt: string;
		description: string;
		totalMembers: number;
		createdByLog: {
			createdAt: string;
			createdBy: {
				profile: {
					fullName: string;
				};
			};
		};
		updatedByLog: {
			createdAt: string;
			createdBy: {
				profile: {
					fullName: string;
				};
			};
		};
	};
	refreshList: () => void;
}

const PatientsGroupsListItem: React.FC<Props> = ({ item, refreshList }) => {
	const { dateFormatMoment } = useAppContext();
	const { displayError } = useApiClient();
	const { t } = useTranslation();
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
	const [isRenameOpen, setIsRenameOpen] = useState(false);
	const [isGroupMembersOpen, setIsGroupMembersOpen] = useState(false);
	const updatedByLog = item?.updatedByLog ?? item?.createdByLog;

	const handleEdit = () => {
		setIsGroupMembersOpen(true);
	};
	const handleRename = () => {
		setIsRenameOpen(true);
	};

	const handleRemove = async () => {
		try {
			const result = await apiClient.delete(`/group/${item.id}`);
			if (result.status === 200) {
				refreshList();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handleAfterRename = () => {
		setIsRenameOpen(false);
		refreshList();
	};

	const handleAfterMembersEdit = () => {
		setIsGroupMembersOpen(false);
		refreshList();
	};

	return (
		<div className={styles.wrapper}>
			{}
			<div className={styles.content}>
				<div className={styles.title}>{item.name}</div>
				<div>{item.description}</div>
				<div className={styles.items}>
					{item.createdByLog && (
						<div id='user-group-list-col-1' className={styles.itemWrapper}>
							<div className={styles.item}>
								<Text className={styles.title} tx='base.createdBy' firstUpper />
								<div className={styles.value}>
									{item?.createdByLog?.createdBy?.profile?.fullName}
								</div>
							</div>
							<div className={styles.item}>
								<Text
									className={styles.title}
									tx='component.groupMsgs.createdDate'
									firstUpper
								/>
								<div className={styles.value}>
									<FormattedMomentDate
										date={item?.createdByLog?.createdAt}
										format={DateFormat.DATE_TIME}
									/>
								</div>
							</div>
						</div>
					)}
					{updatedByLog && (
						<div id='user-group-list-col-2' className={styles.itemWrapper}>
							<div className={styles.item}>
								<Text
									className={styles.title}
									tx='component.groupMsgs.lastEditBy'
									firstUpper
								/>
								<div className={styles.value}>
									{updatedByLog?.createdBy?.profile?.fullName}
								</div>
							</div>
							<div className={styles.item}>
								<Text
									className={styles.title}
									tx='component.groupMsgs.lastEdit'
									firstUpper
								/>
								<div className={styles.value}>
									<FormattedMomentDate
										date={updatedByLog?.createdAt}
										format={DateFormat.DATE_TIME}
									/>
								</div>
							</div>
						</div>
					)}
					<div id='user-group-list-col-3' className={styles.itemWrapper}>
						<div className={styles.item}>
							<Text
								className={styles.title}
								tx='component.groupMsgs.amountOfMembers'
								firstUpper
							/>
							<div className={styles.value}>{item?.totalMembers}</div>
						</div>
					</div>
				</div>
			</div>
			<div className={styles.controls}>
				<Text className={styles.title} tx='base.quickActions' firstUpper />
				<LinkButton
					title='component.groupMsgs.editMembers'
					theme={Theme.BLUE}
					onClick={handleEdit}
					firstUpper
				/>
				<LinkButton
					title='component.groupMsgs.renameGroup'
					theme={Theme.BLUE}
					onClick={handleRename}
					firstUpper
				/>
				<LinkButton
					title='base.history'
					theme={Theme.BLUE}
					onClick={() => setIsHistoryOpen(true)}
					firstUpper
				/>
				{isHistoryOpen && (
					<PatientsGroupsHistoryModal
						isOpen
						onClose={() => setIsHistoryOpen(false)}
						groupId={item.id}
						groupName={item.name}
					/>
				)}
				<RemoveButton
					title='component.groupMsgs.deleteGroup'
					confirmationTitle='component.groupMsgs.groupDeletion'
					confirmationText={t('component.userGroups.remove', {
						groupName: item.name,
					})}
					flashMessage='component.groupMsgs.groupWasDeleted'
					firstUpper
					onClick={handleRemove}
				/>
				<Button
					theme={Theme.GREEN}
					title='component.groupMsgs.sendMsg'
					className={styles.button}
					onClick={() => setIsNewMessageOpen(true)}
				/>
				{isNewMessageOpen && (
					<ActionCenterNewMessageModal
						isOpen
						defaultData={{
							recipients: [
								{
									type: MultiSearchWithCheckListItemType.GROUP,
									value: item.id,
									label: item.name,
								},
							],
						}}
						onClose={() => setIsNewMessageOpen(false)}
					/>
				)}
				{isRenameOpen && (
					<PatientsGroupsRenameModal
						isOpen
						onClose={() => setIsRenameOpen(false)}
						onSuccess={handleAfterRename}
						group={{
							id: item.id,
							name: item.name,
							description: item.description,
						}}
					/>
				)}
				{isGroupMembersOpen && (
					<PatientsGroupsMembersModal
						isOpen
						onClose={handleAfterMembersEdit}
						groupId={item.id}
					/>
				)}
			</div>
		</div>
	);
};

export default PatientsGroupsListItem;
